/* Progressive home media only. Never replace native theme navigation/cart/form handlers. */
(() => {
  'use strict';
  if (window.lkShellLoaded) return;
  window.lkShellLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const measure = () => document.documentElement.style.setProperty(
    '--lk-shell-scrollbar', `${window.innerWidth - document.documentElement.clientWidth}px`
  );
  measure();
  window.addEventListener('resize', measure, {passive: true});

  function init(root) {
    if (instances.has(root)) return;
    const events = new AbortController();
    const signal = events.signal;
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, {...options, signal});
    const track = root.querySelector('[data-lk-hero-track]');
    const slides = track ? Array.from(track.querySelectorAll('[data-lk-slide]')) : [];
    const dots = Array.from(root.querySelectorAll('[data-lk-slide-button]'));
    const pause = root.querySelector('[data-lk-pause]');
    const status = root.querySelector('[data-lk-status]');
    let current = 0;
    let timer;
    let scrollFrame;
    let userPaused = reduceMotion.matches || !root.hasAttribute('data-lk-autoplay');
    let hovered = false;
    let focused = false;
    let visible = true;
    let observer;

    function videoState() {
      slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (index !== current || document.hidden || !visible || userPaused || hovered || focused || reduceMotion.matches) {
          video.pause();
          return;
        }
        if (!video.getAttribute('src') && video.dataset.src) {
          video.src = video.dataset.src;
          video.load();
        }
        video.muted = true;
        const playback = video.play();
        if (playback) playback.catch(() => {}); // Poster remains the fallback.
      });
    }
    function schedule() {
      window.clearTimeout(timer);
      if (slides.length > 1 && !userPaused && !reduceMotion.matches && !hovered && !focused && visible && !document.hidden) {
        timer = window.setTimeout(() => go(current + 1), 10000);
      }
    }
    function update() {
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
      if (status) status.textContent = `${current + 1} / ${slides.length}`;
      if (pause) {
        pause.setAttribute('aria-pressed', String(userPaused));
        pause.setAttribute('aria-label', userPaused ? 'Play slideshow and video' : 'Pause slideshow and video');
        pause.textContent = userPaused ? '▶' : 'Ⅱ';
      }
      videoState();
      schedule();
    }
    function go(index) {
      current = (index + slides.length) % slides.length;
      const target = slides[current];
      const offset = target.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      track.scrollTo({left: offset, behavior: reduceMotion.matches ? 'auto' : 'smooth'});
      update();
    }
    if (slides.length) {
      const controls = root.querySelector('[data-lk-hero-controls]');
      if (controls) controls.hidden = false;
      dots.forEach((button, index) => listen(button, 'click', () => go(index)));
      if (pause) listen(pause, 'click', () => { userPaused = !userPaused; update(); });
      listen(track, 'scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const left = track.getBoundingClientRect().left;
          const nearest = slides.reduce((best, slide, index) =>
            Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(slides[best].getBoundingClientRect().left - left) ? index : best, 0);
          if (nearest !== current) { current = nearest; update(); }
        });
      }, {passive: true});
      const hero = track.parentElement;
      listen(hero, 'mouseenter', () => { hovered = true; videoState(); schedule(); });
      listen(hero, 'mouseleave', () => { hovered = false; videoState(); schedule(); });
      listen(hero, 'focusin', () => { focused = true; videoState(); schedule(); });
      listen(hero, 'focusout', (event) => { focused = hero.contains(event.relatedTarget); videoState(); schedule(); });
      listen(document, 'visibilitychange', update);
      listen(reduceMotion, 'change', () => { if (reduceMotion.matches) userPaused = true; update(); });
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; update(); }, {threshold: .1});
        observer.observe(hero);
      }
      update();
    }

    root.querySelectorAll('[data-lk-rail]').forEach((rail) => {
      const scroller = rail.querySelector('[data-lk-rail-track]');
      const controls = rail.querySelector('[data-lk-rail-controls]');
      if (!scroller || !controls) return;
      controls.hidden = false;
      controls.querySelectorAll('[data-lk-scroll]').forEach((button) => listen(button, 'click', () => {
        const card = scroller.firstElementChild;
        const gap = parseFloat(getComputedStyle(scroller).gap) || 0;
        const rtl = getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;
        scroller.scrollBy({left: Number(button.dataset.lkScroll) * rtl * ((card ? card.clientWidth : 300) + gap), behavior: reduceMotion.matches ? 'auto' : 'smooth'});
      }));
    });

    instances.set(root, () => {
      events.abort();
      window.clearTimeout(timer);
      cancelAnimationFrame(scrollFrame);
      if (observer) observer.disconnect();
      root.querySelectorAll('video').forEach((video) => video.pause());
      instances.delete(root);
    });
  }
  const mount = (scope) => scope.querySelectorAll('[data-lk-home]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), {once: true});
  else mount(document);
  document.addEventListener('shopify:section:load', (event) => { measure(); mount(event.target); });
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((dispose, root) => { if (event.target.contains(root)) dispose(); });
  });
})();